import { FC, useMemo, useState } from 'react';
import { Form, Payment } from '@repo-level/core-front';

import { FieldSelect } from './FieldSelect';
import { FieldPhoneNumber } from './FieldPhoneNumber';
import { FieldAmount } from './FieldAmount';
import { FieldDepositCryptoAddress } from './FieldDepositCryptoAddress';
import { FieldEmail } from './FieldEmail';
import { FieldText } from './FieldText';
import { FieldCreditCardNumber } from './FieldCreditCardNumber';
import { FieldCvvCard } from './FieldCvvCard';
import { FieldCardExpiryDate } from './FieldCardExpiryDate';
import { FieldBanner } from './FieldBanner';
import { FieldCascadeSelect } from './FieldCascadeSelect';
import { FieldPassword } from './FieldPassword';
import { FieldCardHolder } from './FieldCardHolder';
import { ErrorBoundary } from 'components/internal/ErrorBoundary';
import { sendDepositPaymentMethodCrashedEvent } from 'modules/googleAnalytics';

export type PaymentFieldProps = Form.MethodField & {
  paymentAction: Payment.Action;
  minAmountValue: number;
  paymentMethodId: string;
};

export const PaymentField: FC<PaymentFieldProps> = (props) => {
  const [error, setError] = useState<Error | null>(null);

  const Component = useMemo(() => {
    switch (props.type) {
      case Form.MethodFieldType.Tel:
        return <FieldPhoneNumber {...props} />;
      case Form.MethodFieldType.Select:
        return <FieldSelect {...props} />;
      case Form.MethodFieldType.Amount:
        return <FieldAmount {...props} />;
      case Form.MethodFieldType.DepositCryptoAddress:
        return <FieldDepositCryptoAddress {...props} />;
      case Form.MethodFieldType.Email:
        return <FieldEmail {...props} />;
      case Form.MethodFieldType.Text:
      case Form.MethodFieldType.CashoutCryptoAddress:
        return <FieldText {...props} />;
      case Form.MethodFieldType.CardHolder:
        return <FieldCardHolder {...props} />;
      case Form.MethodFieldType.CreditCardNumber:
        return <FieldCreditCardNumber {...props} />;
      case Form.MethodFieldType.Cvv:
        return <FieldCvvCard {...props} />;
      case Form.MethodFieldType.CardExpiryDate:
        return <FieldCardExpiryDate {...props} />;
      case Form.MethodFieldType.Banner:
        return <FieldBanner {...props} />;
      case Form.MethodFieldType.CascadeSelect:
        return <FieldCascadeSelect {...props} />;
      case Form.MethodFieldType.Password:
        return <FieldPassword {...props} />;
      default:
        return null;
    }
  }, [props]);

  if (error) {
    throw error;
  }

  return (
    <ErrorBoundary
      onError={(error) => {
        if (error) setError(error);
        sendDepositPaymentMethodCrashedEvent({
          payment_method_id: props.paymentMethodId,
          field_title: props.label || props.placeholder || '',
          field_name: props.fieldName || '',
          field_type: props.type,
          error: error?.message,
        });
      }}
    >
      {Component}
    </ErrorBoundary>
  );
};
